/**
 * TradeHistory - 거래 기록 컴포넌트
 * CRITICAL: 절대 목업 데이터 사용 금지!
 */
import React, { useCallback, useEffect, useState } from 'react';
import './TradeHistory.css';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';
import { FilterButtons, TradeRow, FilterType, Trade } from './TradeHistoryHelpers';

type Props = {
  market?: 'US' | 'KR';
  scope?: 'paper' | 'live';
  title?: string;
  emptyMessage?: string;
};

const TradeHistory: React.FC<Props> = ({
  market,
  scope = 'paper',
  title = '실현 손익 기록',
  emptyMessage = '데이터 없음',
}) => {
  const [trades, setTrades] = useState<Trade[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('ALL');

  const loadTrades = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filter !== 'ALL') params.append('trade_type', filter);
      if (market) params.append('market', market);
      if (scope) params.append('scope', scope);
      params.append('limit', '50');

      const response = await apiFetch(`${API_BASE_URL}/api/trades/history?${params}`);
      if (!response.ok) throw new Error(`API 오류: ${response.status}`);
      setTrades(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : '거래 기록 조회 중 오류 발생');
    } finally {
      setLoading(false);
    }
  }, [filter, market, scope]);

  useEffect(() => { loadTrades(); }, [loadTrades]);

  if (loading && trades === null) {
    return (
      <div className="trade-history">
        <h3>{title}</h3>
        <div className="loading"><div className="spinner"></div><p>로딩 중...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trade-history">
        <h3>{title}</h3>
        <div className="error"><p>{error}</p><button onClick={loadTrades}>다시 시도</button></div>
      </div>
    );
  }

  if (!trades || trades.length === 0) {
    return (
      <div className="trade-history">
        <h3>{title}</h3>
        <FilterButtons filter={filter} setFilter={setFilter} />
        <div className="empty"><p>{emptyMessage}</p></div>
      </div>
    );
  }

  return (
    <div className="trade-history">
      <div className="history-header">
        <h3>{title}</h3>
        <button onClick={loadTrades} className="refresh-btn">새로고침</button>
      </div>
      <FilterButtons filter={filter} setFilter={setFilter} count={trades.length} />
      <div className="table-wrapper">
        <table className="trades-table">
          <thead>
            <tr>
              <th className="align-left">시간</th>
              <th className="align-left">종목</th>
              <th className="align-center">유형</th>
              <th className="align-right">수량</th>
              <th className="align-right">수익률</th>
              <th className="align-right">수익 금액</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((trade) => <TradeRow key={trade.id} trade={trade} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TradeHistory;
